import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { getFile } from '#src/app/shared/hooks/file';
import { getNavigatorShareInterface } from '#src/app/shared/share/utils';
import { copyToClipboard } from '#src/app/shared/utils/clipboard';

import { trackShare, trackShareFailure } from './track';

type OpenStoriesEducationModalResult = {
	waitForResult: () => Promise<{ successfulShare: boolean } | undefined>;
};

type Options = {
	trackingData: Record<string, string | number | boolean | undefined>;
	trackingEvents: Record<string, string>;
	openStoriesEducationModal: (options: {
		onContinueAction: () => Promise<void>;
		shareVisualPreview: File | string | null;
	}) => OpenStoriesEducationModalResult;
};

export async function shareInstagramStories(
	data: { url: string; image: File | string | null },
	{ trackingEvents, trackingData, openStoriesEducationModal }: Options,
	track: TrackingFn,
	utilityContext: UtilityContext,
): Promise<boolean> {
	const { url } = data;

	const image = await toFile(data.image, utilityContext);

	const navigator = getNavigatorShareInterface();

	if (!image || !navigator) {
		trackShare(trackingEvents.cancel, trackingData, track);
		return false;
	}

	const sharePayload = { files: [image] };

	const triggerNativeShare = async () => {
		try {
			/**
			 * Doing a trigger and forget copy to clipboard to avoid being
			 * hit by a transient user gesture error.
			 */
			void copyToClipboard(url, utilityContext.i18n);

			await navigator.share(sharePayload);

			trackShare(trackingEvents.success, trackingData, track);
		} catch (e) {
			const error = e as Error;

			/**
			 * navigator.share() can reject with AbortError when the user leaves the browser
			 * to complete the share in another app (e.g. selecting Instagram from the native
			 * share sheet). This is expected platform behaviour — not a genuine share failure —
			 * so we silently swallow it to avoid emitting false-positive share_petition_failure
			 * events.
			 *
			 * @see https://change.atlassian.net/browse/CHANGE-93785
			 */
			if (error.name === 'AbortError') {
				return;
			}

			trackShareFailure(
				trackingEvents.failure,
				{
					...trackingData,
					errorName: error.name,
					errorMessage: error.message,
				},
				track,
			);
		}
	};

	trackShare(trackingEvents.intent, trackingData, track);

	const successfulShare = await (async () => {
		try {
			if (!navigator.canShare || !navigator.canShare(sharePayload)) {
				return false;
			}
			const modalResult = await openStoriesEducationModal({
				onContinueAction: triggerNativeShare,
				shareVisualPreview: image,
			}).waitForResult();

			return !!modalResult?.successfulShare;
		} catch (error) {
			return false;
		}
	})();

	trackShare(successfulShare ? trackingEvents.continue : trackingEvents.cancel, trackingData, track);

	return successfulShare;
}

async function toFile(file: File | string | null, utilityContext: UtilityContext): Promise<File | null> {
	if (file instanceof File) {
		return file;
	}
	if (typeof file === 'string') {
		/**
		 * An issue was reported that happens for Android where, if we do not include the file `type` when
		 * triggering the native share, the flow breaks and throws an error.
		 * Adding the `type` by default to avoid this issue across the board.
		 *
		 * @see https://change.atlassian.net/browse/CHANGE-92124
		 */
		const res = await getFile(file, undefined, true);

		if (!res) {
			void utilityContext.errorReporter.report(
				new Error(`Failed to get image file from URL for instagramStories share: ${file}`),
			);
		}

		return res;
	}
	void utilityContext.errorReporter.report(new Error('Missing image for instagramStories share'));
	return null;
}
